import {
  CLIENT_HEARTBEAT_MS,
  makeEnvelope,
  parseEnvelope,
  PROTOCOL_VERSION,
  type ClientAction,
  type ClientPayloadMap,
  type ServerAction,
  type ServerMessage,
  type ServerPayloadMap,
} from "@gaoya/shared";
import type { Platform, SocketHandle } from "../platform/types.js";
import { pushLog, toast, type UiState } from "../game/state.js";

const BACKOFF = [1000, 2000, 4000, 8000];
const DOMAIN_BLOCKED = "微信未放行茶馆线路，暂时进不去";
const FOREGROUND_RECONNECT_MS = 2000;

function isBlockedDomainError(msg: string): boolean {
  const s = msg.toLowerCase();
  return (
    s.includes("not in domain") ||
    s.includes("domain list") ||
    s.includes("不在以下合法域名") ||
    s.includes("不在合法域名")
  );
}

export type SocketListener = {
  onServer: (msg: ServerMessage) => void;
  onOpen: () => void;
  onClose: () => void;
};

export class GameSocket {
  private handle: SocketHandle | null = null;
  private seq = 1;
  private heartbeatId: ReturnType<typeof setInterval> | null = null;
  private reconnectId: ReturnType<typeof setTimeout> | null = null;
  private backoffIdx = 0;
  private wantOpen = false;
  private usedFallback = false;
  private currentUrl = "";
  private urls: { primary: string; fallback: string | null };
  private connGen = 0;
  private hiddenAt: number | null = null;

  constructor(
    private platform: Platform,
    private ui: UiState,
    private listener: SocketListener,
  ) {
    this.urls = platform.resolveWsUrl();
  }

  start(): void {
    this.wantOpen = true;
    if (this.ui.connected) {
      this.listener.onOpen();
      return;
    }
    if (this.handle) {
      if (this.ui.connecting) return;
      try {
        this.handle.close();
      } catch {
        /* ignore */
      }
      this.handle = null;
    }
    this.usedFallback = false;
    this.backoffIdx = 0;
    this.open(this.urls.primary);
  }

  stop(): void {
    this.wantOpen = false;
    this.clearHeartbeat();
    if (this.reconnectId !== null) {
      clearTimeout(this.reconnectId);
      this.reconnectId = null;
    }
    this.handle?.close();
    this.handle = null;
    this.ui.connected = false;
  }

  onBackground(): void {
    this.hiddenAt = this.platform.now();
  }

  onForeground(): void {
    const hiddenAt = this.hiddenAt;
    this.hiddenAt = null;
    if (!this.wantOpen) return;
    if (hiddenAt === null) return;
    if (this.platform.now() - hiddenAt < FOREGROUND_RECONNECT_MS && this.ui.connected) return;
    this.reconnectNow();
  }

  private reconnectNow(): void {
    this.backoffIdx = 0;
    this.usedFallback = false;
    if (this.reconnectId !== null) {
      clearTimeout(this.reconnectId);
      this.reconnectId = null;
    }
    this.clearHeartbeat();
    const old = this.handle;
    this.handle = null;
    this.ui.connected = false;
    try {
      old?.close();
    } catch {
      /* ignore */
    }
    this.open(this.urls.primary);
  }

  send<A extends ClientAction>(action: A, payload: ClientPayloadMap[A]): void {
    const env = makeEnvelope(action, payload, this.seq++, this.platform.now());
    pushLog(this.ui, "out", action);
    this.handle?.send(JSON.stringify(env));
  }

  private open(url: string): void {
    this.currentUrl = url;
    this.ui.connecting = true;
    if (!this.ui.connHint || /wss?:\/\/|\b\/ws\b/i.test(this.ui.connHint)) {
      this.ui.connHint = "正在连线…";
    }
    this.platform.log("ws open", url);
    const gen = ++this.connGen;
    this.handle = this.platform.connectSocket(url, {
      onOpen: () => {
        if (gen !== this.connGen) return;
        this.ui.connecting = false;
        this.ui.connected = true;
        this.ui.connHint = "";
        this.backoffIdx = 0;
        this.startHeartbeat();
        this.listener.onOpen();
      },
      onMessage: (raw) => {
        if (gen !== this.connGen) return;
        this.onRaw(raw);
      },
      onClose: () => {
        if (gen !== this.connGen) return;
        this.onClosed();
      },
      onError: (msg) => {
        if (gen !== this.connGen) return;
        this.platform.log("ws error", msg);
        if (isBlockedDomainError(msg)) {
          this.abortOpen(DOMAIN_BLOCKED);
          return;
        }
        this.ui.connHint = "连线不稳，正在重试";
      },
    });
    if (!this.wantOpen) {
      try {
        this.handle?.close();
      } catch {
        /* ignore */
      }
      this.handle = null;
      this.ui.connecting = false;
    }
  }

  private onRaw(raw: string): void {
    const env = parseEnvelope(raw);
    if (!env) return;
    if (env.v !== PROTOCOL_VERSION) {
      this.platform.log("protocol mismatch", env.v);
    }
    pushLog(this.ui, "in", env.action);
    this.listener.onServer(env as ServerMessage);
  }

  private abortOpen(hint: string): void {
    const already = !this.wantOpen && !this.ui.connecting && !this.handle;
    this.wantOpen = false;
    this.clearHeartbeat();
    if (this.reconnectId !== null) {
      clearTimeout(this.reconnectId);
      this.reconnectId = null;
    }
    try {
      this.handle?.close();
    } catch {
      /* ignore */
    }
    this.handle = null;
    this.ui.connected = false;
    this.ui.connecting = false;
    this.ui.connHint = hint;
    if (!already) toast(this.ui, hint, 4500);
  }

  private onClosed(): void {
    this.clearHeartbeat();
    this.handle = null;
    this.ui.connected = false;
    this.ui.authed = false;
    this.listener.onClose();
    if (!this.wantOpen) {
      this.ui.connecting = false;
      return;
    }

    if (!this.usedFallback && this.urls.fallback && this.currentUrl === this.urls.primary) {
      this.usedFallback = true;
      this.ui.connHint = "回落备用地址…";
      this.open(this.urls.fallback);
      return;
    }

    const wait = BACKOFF[Math.min(this.backoffIdx, BACKOFF.length - 1)] ?? 8000;
    this.backoffIdx = Math.min(this.backoffIdx + 1, BACKOFF.length - 1);
    this.ui.connHint = `${wait / 1000}s 后重连`;
    this.reconnectId = setTimeout(() => {
      this.reconnectId = null;
      const url =
        this.usedFallback && this.urls.fallback ? this.urls.fallback : this.urls.primary;
      this.open(url);
    }, wait);
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatId = setInterval(() => {
      if (this.ui.connected) this.send("HEARTBEAT", {});
    }, CLIENT_HEARTBEAT_MS);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatId !== null) {
      clearInterval(this.heartbeatId);
      this.heartbeatId = null;
    }
  }
}

export function asPayload<A extends ServerAction>(
  msg: ServerMessage,
  _action: A,
): ServerPayloadMap[A] {
  return msg.payload as ServerPayloadMap[A];
}
